import { updatePaymentMethod } from "@/api/rest/checkoutApi";
import { UserRetailerCheckoutOrder } from "@/components/Checkout/types";
import { useTrackActions } from "@/utils/hooks/useTracking";
import { AnalyticsTrackingEvent } from "@brandclub/common-ui";
import { Skeleton } from "@mui/material";
import { useEffect, useState } from "react";
import { useCheckoutUI } from "../../../CheckoutUIProvider";
import { useCheckoutCartInfo } from "../../../hooks/useCheckoutCartInfo";
import { CheckoutIntent } from "../../../types/CheckoutIntent";
import { CheckoutPaymentAddForm } from "./CheckoutPaymentAddForm";
import { CheckoutPaymentSelectForm } from "./CheckoutPaymentSelectForm";

const Loading = () => {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <div>
        <Skeleton
          variant="rectangular"
          height={50}
          sx={{ backgroundColor: "#F6F8FA" }}
        />
      </div>
      <div>
        <Skeleton
          variant="rectangular"
          height={50}
          sx={{ backgroundColor: "#F6F8FA" }}
        />
      </div>
    </div>
  );
};

export const ExperimentalCheckoutPaymentEditForm = ({
  formVisible,
}: {
  formVisible: boolean;
}) => {
  const [showAddPaymentForm, setShowAddPaymentForm] = useState(false);
  const [state, send] = useCheckoutUI();
  const [isLoading, setIsLoading] = useState(false);
  const [trackAction] = useTrackActions();
  // @ts-ignore
  const { checkoutIntent, userRetailerCheckoutOrder } = state.context as {
    checkoutIntent: CheckoutIntent;
    userRetailerCheckoutOrder: UserRetailerCheckoutOrder;
  };
  const checkoutIntentId = checkoutIntent?.checkoutIntentId;
  const orderId = userRetailerCheckoutOrder?.orderId;
  const { items } = useCheckoutCartInfo();
  const isPostSubmission = !!orderId;
  useEffect(() => {
    if (
      !checkoutIntent?.availablePaymentMethods ||
      checkoutIntent?.availablePaymentMethods?.length === 0
    ) {
      setShowAddPaymentForm(true);
    } else {
      setShowAddPaymentForm(false);
    }
  }, [checkoutIntent?.availablePaymentMethods, formVisible]);

  useEffect(() => {
    if (formVisible) {
      trackAction(AnalyticsTrackingEvent.CHECKOUT_STEP_VIEWED, {
        checkoutIntentId: checkoutIntentId,
        stepName: "payment",
        checkoutItems: items || [],
        viewType: "editPaymentMethod",
        checkoutPhase: isPostSubmission ? "post_submission" : "pre_submission",
      });
    }
  }, [formVisible, trackAction, checkoutIntentId, isPostSubmission, items]);
  if (!checkoutIntentId || isLoading) {
    return <Loading />;
  }

  if (showAddPaymentForm) {
    return (
      <CheckoutPaymentAddForm
        onCancel={() => {
          setShowAddPaymentForm(false);
        }}
        onAddNewPaymentMethod={async (paymentMethodType) => {
          setShowAddPaymentForm(false);
          await trackAction(AnalyticsTrackingEvent.CHECKOUT_PAYMENT_ADD, {
            checkoutIntentId: checkoutIntentId!,
            stepName: "payment",
            checkoutItems: items || [],
            checkoutPhase: isPostSubmission
              ? "post_submission"
              : "pre_submission",
            paymentMethodType,
            paymentMethodOperationStatus: "success",
          });
        }}
      />
    );
  }
  return (
    <div>
      <CheckoutPaymentSelectForm
        items={checkoutIntent?.availablePaymentMethods ?? []}
        selectedItem={checkoutIntent?.stripePaymentMethod}
        onAddNewPaymentMethod={() => {
          setShowAddPaymentForm(true);
          trackAction(AnalyticsTrackingEvent.CHECKOUT_PAYMENT_ADD, {
            checkoutIntentId: checkoutIntentId!,
            stepName: "payment",
            checkoutItems: items || [],
            checkoutPhase: isPostSubmission
              ? "post_submission"
              : "pre_submission",
            paymentMethodType: "express",
          });
        }}
        value={checkoutIntent?.stripePaymentMethod?.id ?? ""}
        onChange={async (event, value) => {
          setIsLoading(true);
          const {
            checkoutIntent: updatedCheckoutIntent,
            status,
            errorMessage,
          } = await updatePaymentMethod({
            checkoutIntentId: checkoutIntentId!,
            paymentMethodId: value.id,
          });
          if (status === "success") {
            send({
              type: "SET_CHECKOUT_INTENT",
              data: { checkoutIntent: updatedCheckoutIntent },
            });
            trackAction(AnalyticsTrackingEvent.CHECKOUT_STEP_COMPLETED, {
              checkoutIntentId: checkoutIntentId,
              stepName: "payment",
              checkoutItems: items || [],
              paymentMethodId: value.id,
              paymentMethodType: value.type,
              checkoutPhase: isPostSubmission
                ? "post_submission"
                : "pre_submission",
            });
          } else {
            send({
              type: "SET_CHECKOUT_ERROR",
              data: { error: errorMessage },
            });
            trackAction(AnalyticsTrackingEvent.CHECKOUT_STEP_ERROR, {
              checkoutIntentId: checkoutIntentId,
              stepName: "payment",
              checkoutItems: items || [],
              errorMessage: errorMessage || "Failed to update payment method",
              paymentMethodId: value.id,
              paymentMethodType: value.type,
              checkoutPhase: isPostSubmission
                ? "post_submission"
                : "pre_submission",
            });
          }
          setIsLoading(false);
        }}
      />
    </div>
  );
};
